import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  APPROVAL_VIA_LABEL,
  type EstimateLine,
  formatDate,
  formatDayTime,
  formatPhone,
  LINE_ACTIONS,
  LINE_STATE_COLOR,
  LINE_STATES,
  type LineState,
  type OrderDetail,
  ORDER_SOURCE_LABEL,
  ORDER_SOURCES,
  type OrderSource,
  type Part,
  PAYMENT_METHOD_LABEL,
  RACK_TYPE_FULL,
  RACK_TYPE_SHORT,
  RACK_TYPES,
  type RackType,
  RECEIPT_STATUS_LABEL,
  TERMINAL_STATUSES,
  telHref,
  UNIT_STATE_LABEL,
} from '@reikraft/shared';
import { Dot, Field, Icon, Modal, Panel, PhotoGrid, Segmented, useToast } from '@reikraft/ui';
import { api, errorText, uploadFiles } from '../api';
import { Hint, MasterSelect } from '../components/common';
import { formatMoney, kopToRub, rubToKop } from '../format';
import { useFlag, useTz } from '../session';
import { useOrderAction } from './useOrder';

type P = { order: OrderDetail };

const editable = (o: OrderDetail) => !TERMINAL_STATUSES.includes(o.status);

// ─── Изделие ─────────────────────────────────────────────────────────

export function ProductPanel({ order: o }: P) {
  const [edit, setEdit] = useState(false);
  return (
    <Panel
      title="Изделие"
      bodyless
      actions={
        editable(o) && (
          <button type="button" className="rk-btn rk-btn--sm" onClick={() => setEdit(true)}>
            Изменить
          </button>
        )
      }
    >
      <dl className="rk-dl">
        <div>
          <dt>Автомобиль</dt>
          <dd>{o.car ?? '—'}</dd>
        </div>
        <div>
          <dt>Госномер</dt>
          <dd className="rk-mono">{o.plate ?? '—'}</dd>
        </div>
        <div>
          <dt>VIN</dt>
          <dd className="rk-mono">{o.vin ?? '—'}</dd>
        </div>
        <div>
          <dt>Тип рейки</dt>
          <dd>{o.rackType ? RACK_TYPE_FULL[o.rackType] : '—'}</dd>
        </div>
        <div>
          <dt>Номер на корпусе</dt>
          <dd className="rk-mono">{o.oem ?? '—'}</dd>
        </div>
        <div>
          <dt>Бирка</dt>
          <dd className="rk-mono">
            {o.unit ? (
              <Link to={`/units/${o.unit.id}`} className="rk-accent">
                QR · {o.unit.code}
              </Link>
            ) : (
              'после приёмки'
            )}
          </dd>
        </div>
      </dl>
      {(o.complaint || o.completeness || o.checklist.length > 0) && (
        <div className="rk-panel__foot rk-stack" style={{ ['--rk-gap' as string]: '8px' }}>
          {o.complaint && (
            <div>
              <div className="rk-muted" style={{ fontSize: 12 }}>
                Жалоба клиента
              </div>
              <div style={{ whiteSpace: 'pre-line' }}>{o.complaint}</div>
            </div>
          )}
          {(o.checklist.length > 0 || o.completeness) && (
            <div>
              <div className="rk-muted" style={{ fontSize: 12 }}>
                Комплектность
              </div>
              <div>{[o.checklist.join(', '), o.completeness].filter(Boolean).join('. ')}</div>
            </div>
          )}
        </div>
      )}
      <ProductEditModal order={o} open={edit} onClose={() => setEdit(false)} />
    </Panel>
  );
}

function ProductEditModal({ order: o, open, onClose }: P & { open: boolean; onClose: () => void }) {
  const [f, setF] = useState({ car: '', plate: '', vin: '', oem: '', complaint: '', completeness: '', rackType: null as RackType | null });
  useEffect(() => {
    if (open)
      setF({
        car: o.car ?? '',
        plate: o.plate ?? '',
        vin: o.vin ?? '',
        oem: o.oem ?? '',
        complaint: o.complaint ?? '',
        completeness: o.completeness ?? '',
        rackType: o.rackType,
      });
  }, [open, o]);
  const save = useOrderAction(o.id, () => api.patch<OrderDetail>(`/admin/orders/${o.id}`, { ...f }), 'Сохранено');
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Изделие и автомобиль"
      footer={
        <>
          <button type="button" className="rk-btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="rk-btn rk-btn--primary"
            disabled={save.isPending}
            onClick={() => save.mutate(undefined, { onSuccess: onClose })}
          >
            Сохранить
          </button>
        </>
      }
    >
      <div className="rk-grid-form">
        <Field label="Автомобиль">
          <input className="rk-input" value={f.car} onChange={set('car')} />
        </Field>
        <Field label="Госномер">
          <input className="rk-input rk-input--mono" value={f.plate} onChange={set('plate')} />
        </Field>
        <Field label="VIN">
          <input className="rk-input rk-input--mono" value={f.vin} onChange={set('vin')} />
        </Field>
        <Field label="Номер на корпусе">
          <input className="rk-input rk-input--mono" value={f.oem} onChange={set('oem')} />
        </Field>
      </div>
      <Field label="Тип рейки">
        <Segmented
          label="Тип рейки"
          value={f.rackType}
          onChange={(v) => setF({ ...f, rackType: v })}
          options={RACK_TYPES.map((t) => ({ value: t, label: RACK_TYPE_SHORT[t] }))}
        />
      </Field>
      <Field label="Жалоба клиента">
        <textarea className="rk-textarea" rows={3} value={f.complaint} onChange={set('complaint')} />
      </Field>
      <Field label="Комплектность">
        <input className="rk-input" value={f.completeness} onChange={set('completeness')} />
      </Field>
    </Modal>
  );
}

// ─── Смета ───────────────────────────────────────────────────────────

type LineDraft = Omit<EstimateLine, 'id' | 'doneAt'> & { id?: string };

const COMMON_LINES = [
  'Ремкомплект уплотнений штока',
  'Втулка опорная',
  'Пыльник левый',
  'Пыльник правый',
  'Зубчатая пара',
  'Распределитель (золотник)',
  'Тяга рулевая',
  'Наконечник рулевой',
  'Подшипник вала',
  'Датчик момента ЭУР',
  'Разборка, дефектовка, сборка',
  'Стендовые испытания',
];

export function EstimatePanel({ order: o }: P) {
  const [editing, setEditing] = useState<LineDraft | null>(null);
  const estimateOnline = useFlag('estimate_online');
  const save = useOrderAction(o.id, (lines: LineDraft[]) =>
    api.put<OrderDetail>(`/admin/orders/${o.id}/estimate`, {
      lines: lines.map(({ id, name, state, stateLevel, action, qty, priceKop, included }) => ({
        id,
        name,
        state,
        stateLevel,
        action,
        qty,
        priceKop,
        included,
      })),
    }),
  );
  const canEdit = editable(o) && o.status !== 'ready';
  const lines = o.estimate.lines;
  const forClient = o.kind === 'repair';
  const commit = (next: LineDraft[]) => save.mutate(next);
  const toggle = (l: EstimateLine) => commit(lines.map((x) => (x.id === l.id ? { ...x, included: !x.included } : x)));

  const saveLine = (draft: LineDraft) => {
    const next = draft.id ? lines.map((x) => (x.id === draft.id ? { ...x, ...draft } : x)) : [...lines, draft];
    commit(next as LineDraft[]);
    setEditing(null);
  };
  const removeLine = (draft: LineDraft) => {
    commit(lines.filter((x) => x.id !== draft.id));
    setEditing(null);
  };

  const approvedNote =
    o.estimate.status === 'approved'
      ? `Согласовано ${o.estimate.via ? APPROVAL_VIA_LABEL[o.estimate.via] : ''}${o.estimate.byName ? ` · ${o.estimate.byName}` : ''}${o.estimate.decidedAt ? ` · ${formatDayTime(o.estimate.decidedAt)}` : ''}`
      : null;

  return (
    <section className="rk-panel" id="estimate">
      <div className="rk-panel__head">
        <h2 className="rk-section-title">{forClient ? 'Дефектовка и смета' : 'Дефектовка и работы'}</h2>
        {canEdit && (
          <button
            type="button"
            className="rk-btn rk-btn--sm"
            onClick={() => setEditing({ name: '', state: null, stateLevel: 'warn', action: 'Замена', qty: 1, priceKop: 0, included: true })}
          >
            + Позиция
          </button>
        )}
      </div>
      <div className="rk-table-wrap" style={{ border: 0 }}>
        <div className="rk-table adm-est" style={{ ['--rk-table-min' as string]: '760px' }}>
          <div className="rk-tr rk-tr--head" style={{ gridTemplateColumns: 'var(--cols)', padding: '8px 18px' }}>
            <span />
            <span>Деталь / работа</span>
            <span>Состояние</span>
            <span>Действие</span>
            <span>Кол</span>
            <span className="rk-right">Сумма</span>
            <span />
          </div>
          {lines.map((l) => (
            <div key={l.id} className="rk-tr" style={{ opacity: l.included ? 1 : 0.5 }}>
              <input
                type="checkbox"
                checked={l.included}
                disabled={!canEdit || save.isPending}
                onChange={() => toggle(l)}
                aria-label={`Включить в смету: ${l.name}`}
                style={{ width: 18, height: 18, accentColor: 'var(--accent)' }}
              />
              <span style={{ fontWeight: 600 }}>{l.name}</span>
              <span className="rk-row" style={{ ['--rk-gap' as string]: '6px', fontSize: 13, color: 'var(--text-2)' }}>
                <Dot color={LINE_STATE_COLOR[l.stateLevel]} size="sm" />
                {l.state ?? '—'}
              </span>
              <span style={{ fontSize: 13 }}>{l.action}</span>
              <span className="rk-mono" style={{ fontSize: 13 }}>
                {l.qty || '—'}
              </span>
              <b className="rk-mono rk-right">{l.qty * l.priceKop ? formatMoney(l.qty * l.priceKop) : '—'}</b>
              {canEdit ? (
                <button
                  type="button"
                  className="rk-btn rk-btn--sm rk-btn--icon"
                  style={{ borderColor: 'transparent' }}
                  aria-label={`Изменить: ${l.name}`}
                  onClick={() => setEditing(l)}
                >
                  ✎
                </button>
              ) : (
                <span />
              )}
            </div>
          ))}
          {!lines.length && <div className="rk-empty">Смета пустая. Добавьте найденные дефекты и работы.</div>}
        </div>
      </div>
      <div className="rk-panel__foot rk-row" style={{ justifyContent: 'space-between' }}>
        <span className="rk-muted" style={{ fontSize: 13, flex: '1 1 280px' }}>
          {approvedNote ??
            (forClient
              ? estimateOnline
                ? 'Клиент получает смету по ссылке с фото дефектовки и согласует онлайн. Без согласования работы не начинаются.'
                : 'Согласуйте смету с клиентом по телефону или в чате и отметьте согласование. Без него работы не начинаются.'
              : 'Рейка компании: согласование не нужно, сумма — себестоимость восстановления.')}
        </span>
        <span className="rk-row" style={{ ['--rk-gap' as string]: '12px', alignItems: 'baseline' }}>
          <span className="rk-label">Итого</span>
          <b className="rk-display" style={{ fontSize: 24 }}>
            {formatMoney(o.estimate.totalKop)}
          </b>
        </span>
      </div>
      <LineModal line={editing} onClose={() => setEditing(null)} onSave={saveLine} onRemove={removeLine} />
    </section>
  );
}

function LineModal({
  line,
  onClose,
  onSave,
  onRemove,
}: {
  line: LineDraft | null;
  onClose: () => void;
  onSave: (l: LineDraft) => void;
  onRemove: (l: LineDraft) => void;
}) {
  const [d, setD] = useState<LineDraft | null>(line);
  const [price, setPrice] = useState('');
  useEffect(() => {
    setD(line);
    setPrice(kopToRub(line?.priceKop));
  }, [line]);
  if (!d) return <Modal open={false} onClose={onClose} title="" children={null} />;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!d.name.trim()) return;
    onSave({ ...d, priceKop: rubToKop(price) ?? 0 });
  };
  return (
    <Modal
      open={!!line}
      onClose={onClose}
      title={d.id ? 'Позиция сметы' : 'Новая позиция'}
      footer={
        <>
          {d.id && (
            <button type="button" className="rk-btn rk-btn--danger" style={{ marginRight: 'auto' }} onClick={() => onRemove(d)}>
              Удалить
            </button>
          )}
          <button type="button" className="rk-btn" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" form="line-form" className="rk-btn rk-btn--primary" disabled={!d.name.trim()}>
            Сохранить
          </button>
        </>
      }
    >
      <form id="line-form" onSubmit={submit} className="rk-stack">
        <Field label="Деталь или работа">
          <input className="rk-input" list="common-lines" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} autoFocus />
          <datalist id="common-lines">
            {COMMON_LINES.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
        </Field>
        <div className="rk-grid-form">
          <Field label="Состояние">
            <input
              className="rk-input"
              value={d.state ?? ''}
              onChange={(e) => setD({ ...d, state: e.target.value || null })}
              placeholder="Течь, износ 0,4 мм, норма…"
            />
          </Field>
          <Field label="Оценка">
            <select className="rk-select" value={d.stateLevel} onChange={(e) => setD({ ...d, stateLevel: e.target.value as LineState })}>
              {LINE_STATES.map((s) => (
                <option key={s} value={s}>
                  {{ bad: 'Дефект', warn: 'Износ', ok: 'Норма', none: 'Работа' }[s]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Действие">
            <select className="rk-select" value={d.action} onChange={(e) => setD({ ...d, action: e.target.value })}>
              {LINE_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Кол-во">
            <input
              className="rk-input rk-input--mono"
              type="number"
              min={0}
              max={99}
              value={d.qty}
              onChange={(e) => setD({ ...d, qty: Number(e.target.value) || 0 })}
            />
          </Field>
          <Field label="Цена за ед., ₽">
            <input className="rk-input rk-input--mono" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
          </Field>
        </div>
        <label className="rk-check">
          <input type="checkbox" checked={d.included} onChange={(e) => setD({ ...d, included: e.target.checked })} />
          Включить в смету (снимите — для рекомендаций, от которых клиент может отказаться)
        </label>
      </form>
    </Modal>
  );
}

// ─── Работы для мастера ──────────────────────────────────────────────

export function WorksPanel({ order: o }: P) {
  const tz = useTz();
  const ops = o.estimate.lines.filter((l) => l.included && l.action !== '—');
  const toggle = useOrderAction(o.id, (l: EstimateLine) =>
    api.patch<OrderDetail>(`/admin/orders/${o.id}/estimate/lines/${l.id}`, { done: !l.doneAt }),
  );
  if (!ops.length) return null;
  const done = ops.filter((l) => l.doneAt).length;
  return (
    <section className="rk-panel adm-ops">
      <div className="rk-panel__head">
        <h2 className="rk-section-title">Работы по смете</h2>
        <span className="rk-mono rk-muted" style={{ fontSize: 12 }}>
          {done} из {ops.length}
        </span>
      </div>
      {ops.map((l) => (
        <label key={l.id}>
          <input type="checkbox" checked={!!l.doneAt} disabled={toggle.isPending} onChange={() => toggle.mutate(l)} />
          <span className={l.doneAt ? 'rk-grow is-done' : 'rk-grow'}>
            {l.name} <span className="rk-muted">· {l.action.toLowerCase()}</span>
          </span>
          <span className="rk-mono rk-muted" style={{ fontSize: 11 }}>
            {l.doneAt ? formatDayTime(l.doneAt, tz).split(' ')[1] : ''}
          </span>
        </label>
      ))}
    </section>
  );
}

// ─── Фото ────────────────────────────────────────────────────────────

export function PhotosPanel({ order: o }: P) {
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const estimateOnline = useFlag('estimate_online');
  const b2b = useFlag('b2b_cabinet');
  const add = useOrderAction(
    o.id,
    (ids: string[]) => api.post<OrderDetail>(`/admin/orders/${o.id}/photos`, { fileIds: ids, role: 'defect' }),
    'Фото загружены',
  );
  const remove = useOrderAction(o.id, (fileId: string) => api.del<OrderDetail>(`/admin/orders/${o.id}/photos/${fileId}`));
  const upload = async (files: FileList) => {
    setUploading(true);
    try {
      const refs = await uploadFiles(files);
      add.mutate(refs.map((r) => r.id));
    } catch (e) {
      toast(errorText(e), { error: true });
    } finally {
      setUploading(false);
    }
  };
  return (
    <Panel
      title={`Фотоотчёт · ${o.photos.length}`}
      actions={
        (estimateOnline || (b2b && o.organization)) && (
          <span className="rk-muted" style={{ fontSize: 12 }}>
            Клиент видит фото вместе со сметой
          </span>
        )
      }
    >
      <PhotoGrid
        photos={o.photos}
        onAdd={editable(o) ? upload : undefined}
        uploading={uploading || add.isPending}
        onRemove={editable(o) ? (id) => remove.mutate(id) : undefined}
        labels={(p) => (p.role === 'intake' ? 'ПРИЁМКА' : null)}
        capture
        addLabel="Загрузить"
      />
    </Panel>
  );
}

// ─── Стенд ───────────────────────────────────────────────────────────

export function BenchPanel({ order: o, onNew }: P & { onNew: () => void }) {
  const tz = useTz();
  return (
    <Panel
      title="Стенд"
      actions={
        ['repair', 'stand'].includes(o.status) && (
          <button type="button" className="rk-btn rk-btn--sm" onClick={onNew}>
            + Протокол
          </button>
        )
      }
      bodyless
    >
      <div className="rk-rows">
        {o.bench.map((b) => (
          <div key={b.id} className="rk-row" style={{ padding: '12px 18px', justifyContent: 'space-between' }}>
            <span className="rk-row" style={{ ['--rk-gap' as string]: '10px' }}>
              <Dot color={b.passed ? 'var(--success)' : 'var(--danger)'} />
              <b className="rk-mono">{b.number}</b>
              <span className="rk-muted">
                {formatDayTime(b.testedAt, tz)} · {b.masterName ?? '—'}
              </span>
            </span>
            <span className="rk-row" style={{ ['--rk-gap' as string]: '10px' }}>
              <span className="rk-mono" style={{ fontSize: 12 }}>
                {b.passed
                  ? 'ВСЕ ПАРАМЕТРЫ ОК'
                  : `НЕ ОК: ${b.params
                      .filter((p) => p.result === 'fail')
                      .map((p) => p.name)
                      .join(', ')}`}
              </span>
              <a className="rk-accent" href={`/api/print/orders/${o.id}/bench`} target="_blank" rel="noreferrer">
                Протокол
              </a>
            </span>
          </div>
        ))}
        {!o.bench.length && (
          <div className="rk-panel__body rk-muted">Протокол ещё не заполнен. Без пройденного стенда рейку не выдаём.</div>
        )}
      </div>
    </Panel>
  );
}

// ─── Запчасти ────────────────────────────────────────────────────────

export function PartsPanel({ order: o }: P) {
  const [open, setOpen] = useState(false);
  const [partId, setPartId] = useState('');
  const [qty, setQty] = useState(1);
  const parts = useQuery({ queryKey: ['parts'], queryFn: () => api.get<Part[]>('/admin/parts'), enabled: open });
  const use = useOrderAction(o.id, () => api.post<OrderDetail>(`/admin/orders/${o.id}/parts`, { partId, qty }), 'Списано со склада');
  const back = useOrderAction(o.id, (moveId: string) => api.del<OrderDetail>(`/admin/orders/${o.id}/parts/${moveId}`), 'Списание отменено');
  return (
    <Panel
      title="Списано со склада"
      actions={
        editable(o) && (
          <button type="button" className="rk-btn rk-btn--sm" onClick={() => setOpen(true)}>
            + Списать деталь
          </button>
        )
      }
    >
      {o.parts.length ? (
        <div className="rk-stack" style={{ ['--rk-gap' as string]: '0' }}>
          {o.parts.map((p) => (
            <div key={p.id} className="rk-kv" style={{ borderBottom: '1px dashed var(--line-2)' }}>
              <span style={{ color: 'var(--text)' }}>{p.name}</span>
              <span className="rk-row" style={{ ['--rk-gap' as string]: '8px' }}>
                <span className="rk-mono rk-muted">
                  {p.sku ?? ''} × {p.qty}
                </span>
                {editable(o) && (
                  <button type="button" className="rk-btn rk-btn--sm rk-btn--link" onClick={() => back.mutate(p.id)}>
                    вернуть
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <Hint>Детали со склада на этот заказ не списывали.</Hint>
      )}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Списать деталь на заказ"
        footer={
          <button
            type="button"
            className="rk-btn rk-btn--primary"
            disabled={!partId || use.isPending}
            onClick={() => use.mutate(undefined, { onSuccess: () => setOpen(false) })}
          >
            Списать
          </button>
        }
      >
        <Field label="Деталь">
          <select className="rk-select" value={partId} onChange={(e) => setPartId(e.target.value)}>
            <option value="">— выберите</option>
            {(parts.data ?? []).map((p) => (
              <option key={p.id} value={p.id} disabled={p.qty <= 0}>
                {p.name} {p.sku ? `(${p.sku})` : ''} · остаток {p.qty}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Количество">
          <input
            className="rk-input rk-input--mono"
            type="number"
            min={1}
            value={qty}
            onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
          />
        </Field>
      </Modal>
    </Panel>
  );
}

// ─── Клиент ──────────────────────────────────────────────────────────

export function ClientPanel({ order: o }: P) {
  const phone = o.contactPhone ?? o.customer?.phone ?? o.organization?.contactPhone ?? null;
  const name = o.organization?.name ?? o.customer?.name ?? o.contactName;
  const avito = o.customer?.avitoUrl ?? (o.source === 'avito' ? o.sourceRef : null);
  if (!name) {
    return (
      <Panel title="Клиент">
        <Hint>Рейка компании — восстанавливаем для обменного фонда.</Hint>
      </Panel>
    );
  }
  return (
    <Panel title="Клиент" bodyless>
      <div className="rk-panel__body rk-stack" style={{ ['--rk-gap' as string]: '4px' }}>
        <b style={{ fontSize: 16 }}>
          {o.organization ? (
            <Link to={`/organizations/${o.organization.id}`}>{o.organization.name}</Link>
          ) : o.customer ? (
            <Link to={`/clients/${o.customer.id}`}>{o.customer.name}</Link>
          ) : (
            name
          )}
        </b>
        {o.organization && (o.contactName ?? o.organization.contactName) && (
          <span className="rk-text-2">{o.contactName ?? o.organization.contactName}</span>
        )}
        <span className="rk-mono rk-text-2">{formatPhone(phone)}</span>
        <span className="rk-muted" style={{ fontSize: 13 }}>
          {o.organization ? 'Организация' : 'Частное лицо'}
          {o.customer ? ` · заказов: ${o.customer.ordersCount}` : ''} · {ORDER_SOURCE_LABEL[o.source]}
        </span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: avito ? 'repeat(3, minmax(0,1fr))' : 'repeat(2, minmax(0,1fr))',
          borderTop: '1px solid var(--line)',
        }}
      >
        <a
          href={phone ? telHref(phone) : undefined}
          className="rk-btn"
          style={{ border: 0, borderRight: '1px solid var(--line)' }}
          aria-disabled={!phone}
        >
          Позвонить
        </a>
        <a
          href={phone ? `sms:${phone}` : undefined}
          className="rk-btn"
          style={{ border: 0, borderRight: avito ? '1px solid var(--line)' : 0 }}
          aria-disabled={!phone}
        >
          SMS
        </a>
        {avito && (
          <a href={avito} target="_blank" rel="noreferrer" className="rk-btn" style={{ border: 0 }}>
            Авито
          </a>
        )}
      </div>
    </Panel>
  );
}

// ─── Исполнение ──────────────────────────────────────────────────────

export function ExecutionPanel({ order: o }: P) {
  const masters = useFlag('masters');
  const tz = useTz();
  const patch = useOrderAction(o.id, (body: Record<string, unknown>) => api.patch<OrderDetail>(`/admin/orders/${o.id}`, body), 'Сохранено');
  const [total, setTotal] = useState(kopToRub(o.totalKop));
  useEffect(() => setTotal(kopToRub(o.totalKop)), [o.totalKop]);
  return (
    <Panel title="Исполнение">
      <div className="rk-stack" style={{ ['--rk-gap' as string]: '12px' }}>
        {masters && o.kind !== 'sale' && (
          <Field label="Мастер">
            <MasterSelect value={o.masterId} onChange={(masterId) => patch.mutate({ masterId })} />
          </Field>
        )}
        {o.kind !== 'sale' && (
          <Field label="План выдачи">
            <input
              className="rk-input rk-input--mono"
              type="date"
              value={o.dueDate ?? ''}
              onChange={(e) => patch.mutate({ dueDate: e.target.value || null })}
              style={o.overdue ? { borderColor: 'var(--danger)' } : undefined}
            />
          </Field>
        )}
        <Field label="Источник">
          <select className="rk-select" value={o.source} onChange={(e) => patch.mutate({ source: e.target.value as OrderSource })}>
            {ORDER_SOURCES.map((s) => (
              <option key={s} value={s}>
                {ORDER_SOURCE_LABEL[s]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Сумма заказа, ₽" hint={o.estimate.lines.length ? 'По умолчанию — сумма согласованной сметы' : undefined}>
          <input
            className="rk-input rk-input--mono"
            inputMode="decimal"
            value={total}
            onChange={(e) => setTotal(e.target.value)}
            onBlur={() => {
              const v = rubToKop(total);
              if (v !== o.totalKop) patch.mutate({ totalKop: v });
            }}
          />
        </Field>
        <div className="rk-kv" style={{ padding: 0 }}>
          <span>Создан</span>
          <span className="rk-mono">{formatDayTime(o.createdAt, tz)}</span>
        </div>
        <div className="rk-kv" style={{ padding: 0 }}>
          <span>Принят</span>
          <span className="rk-mono">{o.receivedAt ? formatDayTime(o.receivedAt, tz) : '—'}</span>
        </div>
        {o.needsPickup && (
          <div className="rk-kv" style={{ padding: 0 }}>
            <span>Забор</span>
            <span className="rk-td--stack" style={{ textAlign: 'right', alignItems: 'flex-end' }}>
              <span>
                {[o.pickupAddress, o.pickupDate ? formatDate(o.pickupDate) : null, o.pickupWindow].filter(Boolean).join(' · ') ||
                  'нужно согласовать'}
              </span>
              {o.pickupRouteUrl && (
                <a href={o.pickupRouteUrl} target="_blank" rel="noreferrer" className="rk-btn rk-btn--sm">
                  Маршрут в Яндекс Картах ↗
                </a>
              )}
            </span>
          </div>
        )}
        <div className="rk-kv" style={{ padding: 0 }}>
          <span>Гарантия</span>
          <span className="rk-mono">{o.warrantyUntil ? `до ${formatDate(o.warrantyUntil)}` : 'после выдачи'}</span>
        </div>
      </div>
    </Panel>
  );
}

// ─── Оплата ──────────────────────────────────────────────────────────

export function PaymentsPanel({ order: o, onPay }: P & { onPay: () => void }) {
  const npd = useFlag('npd');
  if (o.kind === 'stock') return null;
  const debt = (o.totalKop ?? 0) - o.paidKop;
  return (
    <Panel
      title="Оплата"
      actions={
        o.totalKop !== null &&
        debt > 0 && (
          <button type="button" className="rk-btn rk-btn--sm" onClick={onPay}>
            + Оплата
          </button>
        )
      }
    >
      <div className="rk-stack" style={{ ['--rk-gap' as string]: '6px' }}>
        <div className="rk-kv" style={{ padding: 0 }}>
          <span>Сумма</span>
          <b className="rk-mono">{formatMoney(o.totalKop)}</b>
        </div>
        <div className="rk-kv" style={{ padding: 0 }}>
          <span>Оплачено</span>
          <span className="rk-mono">{formatMoney(o.paidKop)}</span>
        </div>
        {o.totalKop !== null && (
          <div className="rk-kv" style={{ padding: 0 }}>
            <span>{debt <= 0 ? 'Остаток' : o.status === 'done' || o.status === 'declined' ? 'Долг' : 'К оплате'}</span>
            <b className="rk-mono" style={{ color: debt > 0 && o.status === 'done' ? 'var(--danger-text)' : undefined }}>
              {formatMoney(Math.max(debt, 0))}
            </b>
          </div>
        )}
        {o.payments.map((p) => (
          <div key={p.id} className="rk-kv" style={{ padding: '6px 0 0', borderTop: '1px dashed var(--line-2)', fontSize: 13 }}>
            <span>
              {formatDate(p.paidAt)} · {PAYMENT_METHOD_LABEL[p.method].toLowerCase()}
              {npd && p.receiptStatus !== 'not_required' && (
                <span style={{ color: p.receiptStatus === 'pending' ? 'var(--warn)' : 'var(--muted)' }}>
                  {' '}
                  · {RECEIPT_STATUS_LABEL[p.receiptStatus].toLowerCase()}
                </span>
              )}
            </span>
            <span className="rk-mono">{formatMoney(p.amountKop)}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ─── Обменный фонд ───────────────────────────────────────────────────

export function FundPanel({
  order: o,
  onReserve,
  onIssue,
  onExchange,
  busy,
}: P & { onReserve: () => void; onIssue: () => void; onExchange: () => void; busy: boolean }) {
  const noUnit = useOrderAction(
    o.id,
    () => api.post<OrderDetail>(`/admin/orders/${o.id}/exchange`, { unitId: null }),
    'Заказ оформлен как обмен',
  );
  if (o.kind === 'stock') return null;
  const issued = o.issuedUnit && o.issuedUnit.state === 'at_client';
  return (
    <Panel title="Обменный фонд" raised>
      <div className="rk-stack" style={{ ['--rk-gap' as string]: '12px' }}>
        {o.kind === 'repair' && (
          <>
            <span className="rk-text-2" style={{ fontSize: 13 }}>
              Если клиент забирает рейку из фонда, его рейка после ремонта и стенда поступает на склад и может быть выставлена на продажу.
            </span>
            {editable(o) && o.status !== 'ready' && (
              <>
                <button type="button" className="rk-btn rk-btn--ghost" onClick={onExchange} disabled={busy}>
                  Оформить как обмен
                </button>
                <button
                  type="button"
                  className="rk-btn rk-btn--link"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => noUnit.mutate()}
                  disabled={busy || noUnit.isPending}
                >
                  Оформить без выбора рейки
                </button>
              </>
            )}
          </>
        )}
        {(o.kind === 'exchange' || o.kind === 'sale') && (
          <>
            {o.issuedUnit ? (
              <div className="rk-stack" style={{ ['--rk-gap' as string]: '4px' }}>
                <span>
                  {issued ? 'Выдана клиенту' : 'Зарезервирована'}: <b className="rk-mono">бирка {o.issuedUnit.code}</b>
                </span>
                <span className="rk-muted" style={{ fontSize: 13 }}>
                  {o.issuedUnit.productSku ?? ''} · {o.issuedUnit.rackType ? RACK_TYPE_SHORT[o.issuedUnit.rackType] : ''} ·{' '}
                  {UNIT_STATE_LABEL[o.issuedUnit.state].toLowerCase()}
                  {o.issuedUnit.place ? ` · место ${o.issuedUnit.place}` : ''}
                </span>
              </div>
            ) : (
              <span className="rk-muted" style={{ fontSize: 13 }}>
                Рейка из фонда ещё не выбрана.
              </span>
            )}
            {!issued && editable(o) && (
              <div className="rk-row" style={{ ['--rk-gap' as string]: '8px' }}>
                <button type="button" className="rk-btn rk-btn--ghost" onClick={onReserve} disabled={busy}>
                  {o.issuedUnit ? 'Другая рейка' : 'Выбрать рейку'}
                </button>
                {o.kind === 'exchange' && (
                  <button type="button" className="rk-btn rk-btn--primary" onClick={onIssue} disabled={busy}>
                    Выдать клиенту
                  </button>
                )}
              </div>
            )}
            {o.kind === 'exchange' && o.unit && (
              <span className="rk-muted" style={{ fontSize: 13 }}>
                Рейка клиента (бирка {o.unit.code}) после стенда поступит на склад.
                {o.status === 'done' && !o.unit.productSku && (
                  <>
                    {' '}
                    <Link to={`/stock/new?unit=${o.unit.id}`} className="rk-accent">
                      Выставить на продажу →
                    </Link>
                  </>
                )}
              </span>
            )}
          </>
        )}
      </div>
    </Panel>
  );
}

// ─── Журнал ──────────────────────────────────────────────────────────

export function JournalPanel({ order: o }: P) {
  const [text, setText] = useState('');
  const tz = useTz();
  const add = useOrderAction(o.id, () => api.post<OrderDetail>(`/admin/orders/${o.id}/comments`, { text }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    add.mutate(undefined, { onSuccess: () => setText('') });
  };
  return (
    <Panel title="Журнал" bodyless>
      <ol className="adm-log">
        {o.events.map((e) => (
          <li key={e.id}>
            <time dateTime={e.at}>{formatDayTime(e.at, tz)}</time>
            <span>
              {e.type === 'comment' ? <b>{e.text}</b> : e.text}
              {e.actor && <span className="adm-log__actor"> · {e.actor}</span>}
            </span>
          </li>
        ))}
      </ol>
      <form onSubmit={submit} className="rk-row" style={{ ['--rk-gap' as string]: '8px', padding: '0 18px 14px', flexWrap: 'nowrap' }}>
        <label className="rk-grow">
          <span className="rk-sr">Комментарий</span>
          <input className="rk-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Комментарий для команды" />
        </label>
        <button
          type="submit"
          className="rk-btn rk-btn--light rk-btn--icon"
          aria-label="Добавить комментарий"
          disabled={!text.trim() || add.isPending}
        >
          <Icon name="arrow" />
        </button>
      </form>
    </Panel>
  );
}
